import React, { useMemo } from 'react';
import classnames from 'classnames';
import { Column } from '../../components/layout/grid';
import styles from '../../styles/widgets/my-plan/plan.module.scss';
import { toMoneyStringWithComma } from '../../helpers/formatters/money-format';
import { mergeCodeAndConfigValidationSchema } from '../../helpers/validation/schema-keys';
import { timeframeToString } from '../../helpers/time-format';
import { InputGroup } from '../../components/inputs/input-group';
import { InputWheel as Wheel } from '../../components/inputs/wheel';
import { THeadline } from '../../components/template';
import { PageActionsSection } from '../../components/page-actions-section';
import { insertVarIntoString } from '../../helpers/insert-var-into-string';
import { CostsBreakdown } from '../components/costs-breakdown';
import { PlanDoughnuts } from './components/plan-doughnuts';

const Section = ({ className, children }) => (
  <section className={classnames(styles.section, className)}>{children}</section>
);
export const MyPlan = React.memo(
  ({
    goalsState,
    goalsState: {
      goalsLocation,
      propertyPrice,
      depositPercent,
      depositAmount,
      timeframe,
      depositSavings,
      monthlySaving,
      buyersQuantity
    },
    changeStateValue,
    changeValueOnScrollEnd,
    widgetConfiguration
  }) => {
    const { dataviz, breakdown, controls, pageActions } = widgetConfiguration;
    const { wheels } = controls;
    const subtitle = useMemo(
      () =>
        dataviz.subtitle
          ? insertVarIntoString(dataviz.subtitle, {
              goalsLocation,
              propertyPrice: toMoneyStringWithComma(propertyPrice),
              timeframe: timeframeToString(timeframe)
            })
          : null,
      [dataviz.subtitle, goalsLocation, propertyPrice, timeframe]
    );

    const { BUYING_IN, DEPOSIT, DEPOSIT_PERCENT, MONTHLY_SAVINGS, PROPERTY_PRICE } = useMemo(
      () => mergeCodeAndConfigValidationSchema(),
      []
    );

    return (
      <Column className={styles.block} edge>
        <div className={styles.inner}>
          <div className={styles.content}>
            <THeadline className={styles.headline} title={dataviz.title} subtitle={subtitle} />
            <PlanDoughnuts
              goals={goalsState}
              totalHeading={dataviz.doughnuts.total.heading}
              monthlyHeading={dataviz.doughnuts.monthly.heading}
              emojiEnabled={dataviz.doughnuts.emojiEnabled}
            />

            <div className={styles.columns}>
              <Section>
                <THeadline className={styles['breakdown-heading']} title={breakdown.title} />
                <CostsBreakdown goals={goalsState} configuration={breakdown.configuration} />
              </Section>

              <Section className={styles['controls-container']}>
                <THeadline className={styles['controls-heading']} title={controls.title} />
                <InputGroup className={styles.controls} atid='my-plan-sliders'>
                  <Wheel
                    name='depositSavings'
                    value={depositSavings}
                    label={buyersQuantity > 0 ? wheels.depositSavings.twoByuerslabel : wheels.depositSavings.label}
                    increment={DEPOSIT.ITERATOR}
                    min={DEPOSIT.MIN}
                    max={DEPOSIT.MAX}
                    format={toMoneyStringWithComma}
                    onChange={changeStateValue}
                    onChangeEnd={changeValueOnScrollEnd}
                  />
                  <Wheel
                    name='monthlySaving'
                    value={monthlySaving}
                    label={buyersQuantity > 0 ? wheels.monthlySaving.twoByuerslabel : wheels.monthlySaving.label}
                    increment={MONTHLY_SAVINGS.ITERATOR}
                    min={MONTHLY_SAVINGS.MIN}
                    max={MONTHLY_SAVINGS.MAX}
                    format={toMoneyStringWithComma}
                    suffix='p/m'
                    onChange={changeStateValue}
                    onChangeEnd={changeValueOnScrollEnd}
                  />
                  <Wheel
                    name='propertyPrice'
                    value={propertyPrice}
                    label={wheels.propertyPrice.label}
                    increment={PROPERTY_PRICE.ITERATOR}
                    min={PROPERTY_PRICE.MIN}
                    max={PROPERTY_PRICE.MAX}
                    format={toMoneyStringWithComma}
                    onChange={changeStateValue}
                    onChangeEnd={changeValueOnScrollEnd}
                  />
                  <Wheel
                    name='depositPercent'
                    value={depositPercent}
                    label={wheels.depositPercent.label}
                    suffix='%'
                    increment={DEPOSIT_PERCENT.ITERATOR}
                    min={DEPOSIT_PERCENT.MIN}
                    max={DEPOSIT_PERCENT.MAX}
                    format={{
                      computed: toMoneyStringWithComma
                    }}
                    computed={depositAmount}
                    onChange={changeStateValue}
                    onChangeEnd={changeValueOnScrollEnd}
                  />
                  <Wheel
                    name='timeframe'
                    value={timeframe}
                    time={true}
                    label={wheels.timeframe.label}
                    increment={BUYING_IN.ITERATOR}
                    min={BUYING_IN.MIN}
                    max={BUYING_IN.MAX}
                    format={{
                      value: timeframeToString,
                      computed: 'time'
                    }}
                    onChange={changeStateValue}
                    onChangeEnd={changeValueOnScrollEnd}
                  />
                </InputGroup>
              </Section>
            </div>
          </div>
          {pageActions && <PageActionsSection actionsList={pageActions} />}
        </div>
      </Column>
    );
  }
);
